import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { map } from 'rxjs/operators';
import { TimeUnit, LineChartValues, ChartName, Currencies, getCurrencySymbol } from '../../models';
import { Observable } from 'rxjs';
import { Utils } from '../../shared/utils';

@Injectable({
  providedIn: 'root',
})
export class DashboardChartService {
  private http = inject(HttpClient);

  configUrl = environment.apiUrl;
  startDate: Date;
  endDate: Date;
  dayValues: LineChartValues;
  weekValues: LineChartValues;
  monthValues: LineChartValues;

  getRevenueTimeseries(interval: TimeUnit, dateStart: Date, dateEnd?: Date ) {
    let route = `${this.configUrl}a/r/revenue-timeseries?dateStart=${dateStart.toISOString()}&interval=${interval}`;
    if (dateEnd) {
      route += `&dateEnd=${dateEnd.toISOString()}`;
    }
    return this.http.get<{
      count: number,
      currency: Currencies,
      date: Date,
      fees: number,
    }[]>(route).pipe(
      map( (data) => {
        const dbFirstDay = data?.[0]?.date ? new Date(data[0].date) : undefined;
        let databaseHourTimeDifference: number;
        if (dbFirstDay) {
          const dbFirstDayInTimezone = new Date(dbFirstDay);
          dbFirstDayInTimezone.setMinutes(dbFirstDay.getMinutes() - dbFirstDay.getTimezoneOffset());
          const databaseMillisecondTimeDifference =
            dbFirstDayInTimezone.valueOf() - new Date(dbFirstDayInTimezone.toDateString()).valueOf();
          if (databaseMillisecondTimeDifference > 0 ) {
            databaseHourTimeDifference = databaseMillisecondTimeDifference / 1000 / 60 / 60;
          }
        }
        const currencies = Utils.currenciesOfCategories(data);
        const baseData = {
          ticketNb: {
            name: 'Nombre de billets',
            series: getDatesSerie(dateStart, dateEnd, interval, databaseHourTimeDifference),
          },
        };
        currencies.forEach( (c) => {
          baseData[ 'fees' + c ] = {
            name: 'Frais de service ' + getCurrencySymbol(c) + ' ',
            series: getDatesSerie(dateStart, dateEnd, interval, databaseHourTimeDifference),
          };
        });

        data.forEach((row) => {
          const rowDate = new Date(row.date);
          if (dbFirstDay && interval !== 'hour') {
            rowDate.setMinutes(dbFirstDay.getMinutes() - dbFirstDay.getTimezoneOffset());
          }
          const timestamp = new Date(rowDate).valueOf();
          currencies.forEach( (c) => {
            const feesToUpdate = baseData['fees' + c]?.series?.find((x) => x.timestamp === timestamp);
            if (feesToUpdate && row.fees && row.currency === c) {
              feesToUpdate.value += +(row.fees / 100);
            }
          });
          const countToUpdate = baseData.ticketNb?.series?.find((x) => x.timestamp === timestamp);
          if (countToUpdate) {
            countToUpdate.value += +row.count;
          }
        });
        return baseData;
      }),
    );
  }

  getUserTimeseries(interval: TimeUnit, dateStart: Date, dateEnd?: Date ){
    let route = `${this.configUrl}a/u/user-timeseries?dateStart=${dateStart.toISOString()}&interval=${interval}`;
    if (dateEnd) {
      route += `&dateEnd=${dateEnd.toISOString()}`;
    }
    return this.http.get<{
      count: number,
      date: Date,
    }[]>(route).pipe(
      map( (data) => {
        const dbFirstDay = data?.[0]?.date ? new Date(data[0].date) : undefined;
        let databaseHourTimeDifference: number;
        if (dbFirstDay) {
          const dbFirstDayInTimezone = new Date(dbFirstDay);
          dbFirstDayInTimezone.setMinutes(dbFirstDay.getMinutes() - dbFirstDay.getTimezoneOffset());
          const databaseMillisecondTimeDifference =
            dbFirstDayInTimezone.valueOf() - new Date(dbFirstDayInTimezone.toDateString()).valueOf();
          if (databaseMillisecondTimeDifference > 0 ) {
            databaseHourTimeDifference = databaseMillisecondTimeDifference / 1000 / 60 / 60;
          }
        }
        const baseData = [
          {
            name: 'Nouveau(x) utilisateur(s)',
            series: getDatesSerie(dateStart, dateEnd, interval, databaseHourTimeDifference),
          },
        ];
        data.forEach((row) => {
          const rowDate = new Date(row.date);
          if (dbFirstDay && interval !== 'hour') {
            rowDate.setMinutes(dbFirstDay.getMinutes() - dbFirstDay.getTimezoneOffset());
          }
          const timestamp = new Date(rowDate).valueOf();
          const countToUpdate = baseData[0]?.series?.find((x) => x.timestamp === timestamp);
          if (countToUpdate) {
            countToUpdate.value += +row.count;
          }
        });
        return baseData;
      }),
    );
  }

  getWaitlistTimeseries(interval: TimeUnit, dateStart: Date, dateEnd?: Date ){
    let route = `${this.configUrl}a/u/waitlist-timeseries?dateStart=${dateStart.toISOString()}&interval=${interval}`;
    if (dateEnd) {
      route += `&dateEnd=${dateEnd.toISOString()}`;
    }
    return this.http.get<{
      count: number,
      date: Date,
    }[]>(route).pipe(
      map( (data) => {
        const dbFirstDay = data?.[0]?.date ? new Date(data[0].date) : undefined;
        let databaseHourTimeDifference: number;
        if (dbFirstDay) {
          const dbFirstDayInTimezone = new Date(dbFirstDay);
          dbFirstDayInTimezone.setMinutes(dbFirstDay.getMinutes() - dbFirstDay.getTimezoneOffset());
          const databaseMillisecondTimeDifference =
            dbFirstDayInTimezone.valueOf() - new Date(dbFirstDayInTimezone.toDateString()).valueOf();
          if (databaseMillisecondTimeDifference > 0 ) {
            databaseHourTimeDifference = databaseMillisecondTimeDifference / 1000 / 60 / 60;
          }
        }
        const baseData = [
          {
            name: "Inscription(s) en liste d'attente",
            series: getDatesSerie(dateStart, dateEnd, interval, databaseHourTimeDifference),
          },
        ];
        data.forEach((row) => {
          const rowDate = new Date(row.date);
          if (dbFirstDay && interval !== 'hour') {
            rowDate.setMinutes(dbFirstDay.getMinutes() - dbFirstDay.getTimezoneOffset());
          }
          const timestamp = new Date(rowDate).valueOf();
          const countToUpdate = baseData[0]?.series?.find((x) => x.timestamp === timestamp);
          if (countToUpdate) {
            countToUpdate.value += +row.count;
          }
        });
        return baseData;
      }),
    );
  }

  getTicketsStatsByEventAndPeriod(
    eventId: number,
    dateRangeStart: string,
    dateRangeEnd: string,
    type: TimeUnit = 'day',
  ): Observable<LineChartValues> {
    this.dayValues = null;
    this.weekValues = null;
    this.monthValues = null;
    const route = `${this.configUrl}s/e/${eventId}/tickets/period`;
    const params = new HttpParams({
      fromObject: {
        dateRangeStart,
        dateRangeEnd,
      },
    });
    return this.http.get<Array<Array<{day: string, count: number}>>>(route, {params}).pipe(
      map(([onsaleTickets, soldTickets, waitLists]) => {
        this.startDate = new Date(parseInt(dateRangeStart, 10));
        this.endDate = new Date(parseInt(dateRangeEnd, 10));
        const returnedValue = initSeries(this.startDate, this.endDate, 'day');
        onsaleTickets.forEach((row) => {
          const date = dayFormat(new Date(row.day));
          const valueToUpdate = returnedValue[0]?.series?.find((x) => x.name === date);
          if (valueToUpdate) {
            valueToUpdate.value += +row.count;
          }
        });
        soldTickets.forEach((row) => {
          const date = dayFormat(new Date(row.day));
          const valueToUpdate = returnedValue[1]?.series?.find((x) => x.name === date);
          if (valueToUpdate) {
            valueToUpdate.value += +row.count;
          }
        });
        waitLists.forEach((row) => {
          const date = dayFormat(new Date(row.day));
          const valueToUpdate = returnedValue[2]?.series?.find((x) => x.name === date);
          if (valueToUpdate) {
            valueToUpdate.value += +row.count;
          }
        });
        this.dayValues = returnedValue;
        switch (type) {
        case 'hour':
          throw new Error('hours graph not implemented');
        case 'day':
          return this.dayValues;
        case 'week':
          return this.getWeekValues();
        case 'month':
          return this.getMonthValues();
        }
      }),
    );
  }

  getDayValues(): LineChartValues {
    return this.dayValues;
  }

  getWeekValues(): LineChartValues {
    if (!this.weekValues) {
      this.setWeekValues();
    }
    return this.weekValues;
  }

  getMonthValues(): LineChartValues {
    if (!this.weekValues) {
      this.setMonthValues();
    }
    return this.monthValues;
  }

  setWeekValues(): void {
    const returnedValue = initSeries(this.startDate, this.endDate, 'week');
    fillSeries(returnedValue, this.dayValues, false);
    this.weekValues = returnedValue;
  }

  setMonthValues(): void {
    const returnedValue = initSeries(this.startDate, this.endDate, 'month');
    fillSeries(returnedValue, this.dayValues, true);
    this.monthValues = returnedValue;
  }
}

const fillSeries = (
  targetSeries: Array<{name: ChartName, series: Array<{name: string, value: number}>}>,
  sourceDaySeries: Array<{name: ChartName, series: Array<{name: string, value: number}>}>,
  needMonthConversion: boolean = false,
) => {
  // on sale tickets
  let onSaleIndex: number;
  sourceDaySeries[0].series.forEach((stat: {name: string, value: number}) => {
    const index = targetSeries[0].series.findIndex((x) => {
      const nameToCompare = needMonthConversion ? convertToMonthFormat(stat.name) : stat.name;
      return x.name === nameToCompare;
    });
    if (index > -1) {
      onSaleIndex = index;
    }
    targetSeries[0].series[onSaleIndex].value += stat.value;
  });
  // sold tickets
  let soldIndex: number;
  sourceDaySeries[1].series.forEach((stat: {name: string, value: number}) => {
    const index = targetSeries[1].series.findIndex((x) => {
      const nameToCompare = needMonthConversion ? convertToMonthFormat(stat.name) : stat.name;
      return x.name === nameToCompare;
    });
    if (index > -1) {
      soldIndex = index;
    }
    targetSeries[1].series[soldIndex].value += stat.value;
  });
  // waiting list entries
  let entriesIndex: number;
  sourceDaySeries[2].series.forEach((stat: {name: string, value: number}) => {
    const index = targetSeries[2].series.findIndex((x) => {
      const nameToCompare = needMonthConversion ? convertToMonthFormat(stat.name) : stat.name;
      return x.name === nameToCompare;
    });
    if (index > -1) {
      entriesIndex = index;
    }
    targetSeries[2].series[entriesIndex].value += stat.value;
  });
};

const initSeries = (startDate: Date, endDate: Date, type: TimeUnit) => [
  {
    name: 'Billets en vente',
    series: getDatesSerie(startDate, endDate, type),
  },
  {
    name: 'Billets vendus',
    series: getDatesSerie(startDate, endDate, type),
  },
  {
    name: 'Inscriptions à la liste d\'attente',
    series: getDatesSerie(startDate, endDate, type),
  },
] as LineChartValues;

const getDatesSerie = (start: Date, end: Date, type: TimeUnit, timeDifference?: number) => {
  switch (type) {
  case 'hour':
    return getHoursSerie(start, end);
  case 'day':
    return getDaysSerie(start, end, timeDifference);
  case 'week':
    return getWeeksSerie(start, end, timeDifference);
  case 'month':
    return getMonthsSerie(start, end, timeDifference);
  }
};

const getHoursSerie = (start: Date, end: Date) => {
  start.setHours(start.getHours(),0,0,0);
  const serie = new Array<{name: string, value: number, timestamp: number}>();
  for (const dt = new Date(start); dt <= end; dt.setHours(dt.getHours() + 1)) {
    const dateToAdd = new Date(dt);
    const formatedDate = dateToAdd.toLocaleTimeString() + ' ' + dayFormat(dateToAdd);
    serie.push({
      name: formatedDate,
      value: 0,
      timestamp: dateToAdd.valueOf(),
    });
  }
  return serie;
};

const getDaysSerie = (start: Date, end: Date, timeDifference?: number) => {
  if (timeDifference) {
    start.setHours(timeDifference,0,0,0);
  }
  const serie = new Array<{name: string, value: number, timestamp}>();
  for (const dt = new Date(start); dt <= end; dt.setDate(dt.getDate() + 1)) {
    const dateToAdd = new Date(dt);
    const formatedDate = dayFormat(dateToAdd);
    serie.push({
      name: formatedDate,
      value: 0,
      timestamp: dateToAdd.valueOf(),
    });
  }
  return serie;
};

const getWeeksSerie = (start: Date, end: Date, timeDifference?: number) => {
  if (timeDifference) {
    start.setHours(timeDifference,0,0,0);
    start.setDate(start.getDate() - (start.getDay() + 6) % 7);
  }
  const serie = new Array<{name: string, value: number, timestamp: number}>();
  for (const dt = new Date(start); dt <= end; dt.setDate(dt.getDate() + 7)) {
    const dateToAdd = new Date(dt);
    const formatedDate = dayFormat(dateToAdd);
    serie.push({
      name: formatedDate,
      value: 0,
      timestamp: dateToAdd.valueOf(),
    });
  }
  return serie;
};

const getMonthsSerie = (start, end, timeDifference?: number) => {
  if (timeDifference) {
    start.setHours(timeDifference,0,0,0);
    start.setDate(1);
  }
  const serie = new Array<{name: string, value: number, timestamp: number}>();
  for (const dt = new Date(start); dt <= end; dt.setMonth(dt.getMonth() + 1)) {
    const dateToAdd = new Date(dt);
    const formatedDate = convertToMonthFormat(dayFormat(dateToAdd));
    serie.push({
      name: formatedDate,
      value: 0,
      timestamp: dateToAdd.valueOf(),
    });
  }
  return serie;
};

const dayFormat = (date: Date): string => ('0' + date.getDate()).slice(-2) + '/' + ('0' + (date.getMonth() + 1)).slice(-2) + '/' + date.getFullYear().toString().slice(-2);
const convertToMonthFormat = (frenchDateString: string): string => {
  // frenchDateString has following format : 31/12/20 for 31th of December 2020
  const months = ['Jan.', 'Fev.', 'Mars', 'Avril', 'Mai', 'Juin', 'Juil.', 'Août', 'Sept.', 'Oct.', 'Nov.', 'Déc.'];
  // we need to substract 1 as javascript arrays begin at index 0
  const monthIndex = parseInt(frenchDateString.slice(3, 5), 10) - 1;
  const monthLabel = months[monthIndex];
  const year = frenchDateString.slice(-2);
  return monthLabel + ' ' + year;
};
